import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Button, Drawer, Stack, Typography } from "@mui/material";
import { AsyncContentMui, ProgressOverlay, SimpleHeadMui } from "components";
import { defaultState, FormContext, State } from "components/Characteristics/Deal-form-drawer";
import { CategoriesTabs } from "components/Characteristics/Deal-form-drawer/CategoriesTabs";
import { Category } from "components/Characteristics/Deal-form-drawer/Category";
import { CreateChar } from "components/Characteristics/Deal-form-drawer/CreateChar";
import { useCharDataHooks } from "components/Characteristics/Deal-form-drawer/hooks/use-char-data-hooks";
import { useCharHooks } from "components/Characteristics/Deal-form-drawer/hooks/use-char-hooks";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook } from "hooks";
import { useClientActions } from "store";

interface Props {
  open: boolean;
  clientId: number;
  clientName: string;
  canEdit: boolean;
  onClose: () => void;
}

/**
 * Client Manage-Characteristics drawer — a copy of the deal DealFormDrawer that reuses the exact same
 * FormContext + Category + per-data_type field components. Only the data source differs: it fetches
 * GET /clients/{id}/characteristics/data and saves POST /clients/{id}/characteristics/data (client_id in
 * the URL). Deal-only chrome (ViewSelector / Override / stages / asset-class) is omitted — clients have none.
 */
export const ClientCharacteristicsDrawer: React.FC<Props> = ({ open, clientId, clientName, canEdit, onClose }) => {
  const { getClientCharacteristicsDataAction, postClientCharacteristicsDataAction } = useClientActions();

  const [state, setState] = useState<State>(defaultState);

  const { createChar, updateChar, deleteChar, getCharacteristics } = useCharHooks({ state, setState });
  const {
    updateCharData,
    buildNewCharData,
    handleManyCharData,
    getOneData,
    getManyData,
    overrideCharacteristicData,
    isOverrideAvailable
  } = useCharDataHooks({ state, setState });

  const { action: getData, loading } = useActionHook(getClientCharacteristicsDataAction, {
    onSuccess: (res) => {
      // Trimmed client response — only characteristics/characteristic_data/categories. Deal-only State
      // fields (asset class, subcategories, contact, debt) fall back to defaultState.
      setState({
        ...defaultState,
        category: res.categories[0] ?? "General",
        categories: res.categories,
        characteristics: res.characteristics.map((item) => ({ ...item, is_deleted: false })) as State["characteristics"],
        data: res.characteristic_data.map((item) => ({
          ...item,
          deal_id: item.deal_id ?? 0,
          is_deleted: false
        })) as State["data"]
      });
    }
  });

  const { action: postData, loading: saving } = useActionHook(postClientCharacteristicsDataAction);

  useEffect(() => {
    if (open) {
      getData(clientId).catch((err) => console.error("[ClientCharacteristicsDrawer] load failed:", err));
    } else {
      setState(defaultState);
    }
  }, [open, clientId]);

  const handleChangeTab = (value: string) => setState((prev) => ({ ...prev, category: value }));

  const handleSubmit = async () => {
    try {
      await postData(clientId, {
        characteristics: state.characteristics,
        characteristic_data: state.data
      });
      toast.success("Characteristics saved");
      onClose();
    } catch (err) {
      console.error("[ClientCharacteristicsDrawer] save failed:", err);
      toast.error("Failed to save characteristics");
    }
  };

  const formContextValue = useMemo(
    () => ({
      getCharacteristics,
      getOneData,
      getManyData,
      createChar,
      updateChar,
      deleteChar,
      updateCharData,
      buildNewCharData,
      handleManyCharData,
      setCharacteristicRef: () => undefined,
      overrideCharacteristicData,
      isOverrideAvailable,
      viewBy: null
    }),
    [
      getCharacteristics,
      getOneData,
      getManyData,
      createChar,
      updateChar,
      deleteChar,
      updateCharData,
      buildNewCharData,
      handleManyCharData,
      overrideCharacteristicData,
      isOverrideAvailable
    ]
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        ".MuiPaper-root": {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "stretch",
          borderTopRightRadius: "25px",
          borderBottomRightRadius: "25px",
          height: "100%",
          overflow: "hidden",
          minWidth: "550px",
          maxWidth: "550px",
          backgroundColor: "white"
        }
      }}
    >
      <SimpleHeadMui onIconClick={onClose}>Client Characteristics</SimpleHeadMui>
      <AsyncContentMui isLoading={loading} spinnerProps={{ size: 50 }}>
        <FormContext.Provider value={formContextValue}>
          <ProgressOverlay
            inProgress={saving}
            containerSX={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              padding: "20px 0px 20px 20px",
              overflow: "auto",
              gap: "20px"
            }}
          >
            <MaterialTooltip title={clientName} isShow>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "500",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  paddingRight: "15px"
                }}
              >
                {clientName}
              </Typography>
            </MaterialTooltip>

            <CategoriesTabs
              categories={state.categories}
              value={state.category}
              onChange={handleChangeTab}
              showExtraTabs={false}
            />
            <Stack sx={{ overflow: "auto", padding: "10px 20px 10px 0", gap: "20px" }}>
              <Category categoryName={state.category} viewOnly={!canEdit} />
              {canEdit && <CreateChar />}
            </Stack>
          </ProgressOverlay>
        </FormContext.Provider>

        {canEdit && (
          <Stack
            sx={{
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
              height: "87px",
              padding: "30px",
              boxShadow: "0 -4px 13px 0 rgba(10, 49, 66, 0.2)",
              gap: "15px"
            }}
          >
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button disabled={saving} variant="contained" onClick={handleSubmit}>
              Apply
            </Button>
          </Stack>
        )}
      </AsyncContentMui>
    </Drawer>
  );
};
